import type { DocumentMetadataItem, SectionReadinessOptions } from '@lds-efsp/shared-contracts';

/**
 * Build the court-scoped reference data the guided-section readiness predicates
 * consult (SPEC-SOR-174 R9), from the one policy source the wizard holds.
 *
 * `docTypeMetadata` is published by DocumentsTab after its document-types fetch
 * (`registerDocTypeMetadata`), keyed by document-type code — so its KEYS are
 * exactly "the document-type codes the court and case scope offers", which is
 * what R9's court-scoping conjunct is about. Without this supplier that conjunct
 * silently degraded to "the document is typed at all": nothing in the shipped
 * app passed `availableDocTypeCodes`, so the only callers were a proptest helper
 * and a unit test and the court-scoped bar was enforced nowhere a filer could
 * reach.
 *
 * The empty map is deliberately mapped to `undefined` rather than `[]`. An empty
 * list is a positive claim — "this court offers no document type" — and would
 * refuse every typed document; absent is the total-function fallback the
 * predicate already documents (any type satisfies the conjunct). The map is
 * empty in two ordinary situations, so this is not a corner case: before the
 * fetch resolves, and on a step where DocumentsTab was never mounted (a filer
 * who resumes a draft straight onto Confirm). In both the court's list is
 * genuinely unknown here, not known-to-be-empty.
 *
 * One builder shared by both consumers — the context's `validateTab` /
 * `getTabValidationErrors` (which decide the blocker WORDING) and
 * `SectionConfirmControl` (which decides whether Confirm is OFFERED) — because
 * the two disagreeing is precisely the dead stall the R76/R77 blocker
 * discipline exists to prevent.
 */
export function buildSectionReadinessOptions(
  docTypeMetadata: Record<string, DocumentMetadataItem[]>,
): SectionReadinessOptions {
  const codes = Object.keys(docTypeMetadata);
  return {
    metadataByDocType: docTypeMetadata,
    availableDocTypeCodes: codes.length > 0 ? codes : undefined,
  };
}
